import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AVATARS_STORAGE_BUCKET } from "@/features/profiles";
import { useContainerMembersDialog } from "./api/container-members-dialog-hooks";
import { useManageMembersData } from "./api/use-manage-members-data";
import type {
  ContainerMembersDialogProps,
  CreateModeProps,
} from "./container-members-dialog.types";

import { MembersSelectList } from "@/components/lists/members-select-list";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import Avatar from "@/features/avatar/components/avatar";
import { useEffect } from "react";
import { DefaultPersonaToggle } from "./default-persona-toggle";

/**
 * Dual-mode implementation, module-private on purpose (CR-177): manage mode is
 * reachable only through ManageMembersDialog below, so no entry point can feed
 * it hand-built props again (the sidebar once showed a different dialog).
 */
function MembersDialogView(props: ContainerMembersDialogProps) {
  const { open, onOpenChange, containerLabel, mode } = props;
  const isCreateMode = mode === "create";
  const avatarStorageName = mode === "manage" ? props.avatarStorageName : "";

  const {
    isShared,
    isCurrentUserOwner,
    handleToggleShared,
    selectedHumanIds,
    setSelectedHumanIds,
    selectedAiIds,
    setSelectedAiIds,
    humanSelectionItems,
    aiSelectionItems,
    canAdd,
    currentMembers,
    isBusy,
    errorMessage,
    isCreating,
    handleCreate,
    isAdding,
    handleAddSelected,
    removingMemberId,
    handleRemoveMember,
    showDefaultAiPersonaMarker,
    getDefaultPersonaState,
    handleToggleDefaultAiPersona,
    defaultAiPersonaStatus,
  } = useContainerMembersDialog(props);

  // The human picker hides while the container is shared with every project
  // member; personas are always explicit members (CR-177), so the AI picker —
  // and therefore the add section — is always shown.
  const showHumanPicker = !isShared;

  const dialogDescription = isCreateMode
    ? `Select project members and AI assistants to add to this ${containerLabel}, or use the switch to share it with every project member.`
    : isCurrentUserOwner
      ? `View current members, add or remove members, and manage sharing for this ${containerLabel}.`
      : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={isCreateMode ? "sm:max-w-170" : "sm:max-w-190"}>
          <DialogHeader>
            <DialogTitle>
              {isCreateMode
                ? `Create ${containerLabel}`
                : isCurrentUserOwner
                  ? `Manage ${containerLabel} members`
                  : "Members"}
            </DialogTitle>
            {!!dialogDescription && <DialogDescription>{dialogDescription}</DialogDescription>}
          </DialogHeader>

          {/* Sharing switch — owner only, also enforced by the database (CR-205) */}
          {isCreateMode || isCurrentUserOwner ? (
            <div className="space-y-3">
              <div
                className={
                  isCreateMode
                    ? "flex items-center gap-3"
                    : "flex items-center gap-3 rounded-md border p-3"
                }
              >
                <Switch
                  id="shared-toggle"
                  checked={isShared}
                  onCheckedChange={handleToggleShared}
                  disabled={isBusy}
                  className="cursor-pointer"
                />
                <div>
                  <Label htmlFor="shared-toggle" className="cursor-pointer">
                    Share with all project members
                  </Label>
                  {!isCreateMode && (
                    <p className="text-muted-foreground text-xs">
                      {isShared
                        ? `All project members can access this ${containerLabel}.`
                        : `Only explicitly added members can access this ${containerLabel}.`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Create mode: member pickers for whichever classes are not shared */}
          {!!isCreateMode && (
            <div className={showHumanPicker ? "grid gap-4 sm:grid-cols-2" : "grid gap-4"}>
              {showHumanPicker && (
                <MembersSelectList
                  title="Project members"
                  members={humanSelectionItems}
                  selectedMemberIds={selectedHumanIds}
                  onSelectedMemberIdsChange={setSelectedHumanIds}
                  emptyMessage="No project members available."
                  disabled={isBusy}
                />
              )}
              <MembersSelectList
                title="AI assistants"
                members={aiSelectionItems}
                selectedMemberIds={selectedAiIds}
                onSelectedMemberIdsChange={setSelectedAiIds}
                emptyMessage="No AI assistants available."
                disabled={isBusy}
              />
            </div>
          )}

          {/* Manage mode: current members and the add pickers */}
          {!isCreateMode && (
            <div className="grid gap-4 sm:grid-cols-2">
              <section className="space-y-2">
                <h3 className="text-sm font-medium">Current members</h3>

                {/* Permanently mounted: several screen readers ignore a live
                    region that appears together with its first content. Mirrors
                    the visible star for anyone who cannot see it move, and also
                    announces a change another member made. */}
                <div className="sr-only" role="status">
                  {defaultAiPersonaStatus ? <span>{defaultAiPersonaStatus}</span> : null}
                </div>

                <div className="max-h-72 space-y-2 overflow-y-auto rounded-md border p-3">
                  {currentMembers.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      No members in this {containerLabel}.
                    </p>
                  ) : (
                    currentMembers.map((member) => (
                      <div key={member.id} className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <Avatar
                            avatarPath={member.avatarPath}
                            fallbackInitials={member.initials}
                            size={40}
                            storageName={avatarStorageName}
                            statusDot={member.activityStatus}
                          />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{member.name}</p>
                            <p className="text-muted-foreground truncate text-xs">
                              {member.subtitle}
                            </p>
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-1">
                          {showDefaultAiPersonaMarker && member.aiPersonaId ? (
                            <DefaultPersonaToggle
                              aiPersonaId={member.aiPersonaId}
                              personaName={member.name}
                              state={getDefaultPersonaState(member.aiPersonaId)}
                              onToggle={handleToggleDefaultAiPersona}
                            />
                          ) : null}

                          {member.isOwner ? (
                            <span className="text-muted-foreground rounded-full border px-2 py-0.5 text-xs font-medium">
                              Owner
                            </span>
                          ) : member.me && member.removable ? (
                            <Button
                              type="button"
                              variant="outline-destructive"
                              size="sm"
                              onClick={() => void handleRemoveMember(member.id, true)}
                              disabled={isBusy}
                            >
                              {removingMemberId === member.id
                                ? "Leaving..."
                                : `Leave ${containerLabel}`}
                            </Button>
                          ) : member.removable ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => void handleRemoveMember(member.id, false)}
                              disabled={isBusy}
                            >
                              {removingMemberId === member.id ? "Removing..." : "Remove"}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Kept next to the rows it refers to: the marker and the
                    remove buttons both report here, and an alert below the
                    whole grid sits off-screen once the list scrolls.

                    Gated on `!== null`, not on truthiness: the messages are
                    catalogued, and a catalog lookup can yield "" — which would
                    silently swallow the whole alert. */}
                {errorMessage !== null && (
                  <Alert variant="destructive">
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                )}
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-medium">Add members</h3>
                <div className="space-y-3 rounded-md border p-3">
                  {showHumanPicker && (
                    <MembersSelectList
                      title="Project members"
                      members={humanSelectionItems}
                      selectedMemberIds={selectedHumanIds}
                      onSelectedMemberIdsChange={setSelectedHumanIds}
                      emptyMessage="No additional members available."
                      disabled={isBusy}
                    />
                  )}
                  <MembersSelectList
                    title="AI assistants"
                    members={aiSelectionItems}
                    selectedMemberIds={selectedAiIds}
                    onSelectedMemberIdsChange={setSelectedAiIds}
                    emptyMessage="No additional AI assistants available."
                    disabled={isBusy}
                  />
                </div>
              </section>
            </div>
          )}

          {/* Manage mode renders its own alert inside the members section. */}
          {isCreateMode && errorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            {isCreateMode ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isBusy}
              >
                Cancel
              </Button>
            ) : null}
            {isCreateMode ? (
              <Button
                type="button"
                onClick={() => void handleCreate()}
                disabled={isBusy}
                data-testid="container-members-create"
              >
                {isCreating ? "Creating..." : `Create ${containerLabel}`}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => void handleAddSelected()}
                disabled={!canAdd || isBusy}
              >
                {isAdding ? "Adding..." : "Add selected"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Create a chat, message room or document with its initial members. */
export function ContainerMembersDialog(props: CreateModeProps) {
  return <MembersDialogView {...props} />;
}

type ManageMembersDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The chat, message room or document whose members are managed. */
  containerId: string;
  /** Its parent project — the roster the "Add members" picker offers. */
  projectId: string;
};

/**
 * CR-177: the ONLY way to manage an existing container's members. Every entry
 * point (chat / message / document header, sidebar row menu) renders this with
 * just the container and project ids; sharing, AI-chat-ness, default persona and
 * members all come from useManageMembersData, so no caller can feed it a
 * partial picture.
 */
export function ManageMembersDialog({
  open,
  onOpenChange,
  containerId,
  projectId,
}: ManageMembersDialogProps) {
  const { container, containerLabel, isError, roster, onRemoveMember } = useManageMembersData(
    containerId,
    projectId,
  );
  const cannotOpen = isError || (!!container && !containerLabel);

  // Nothing to show (row unreadable, or a container type without a members
  // dialog): hand the open state back so the entry point can be used again
  // instead of staying "open" with nothing on screen.
  useEffect(() => {
    if (open && cannotOpen) {
      onOpenChange(false);
    }
  }, [open, cannotOpen, onOpenChange]);

  // Mount only once the container is known: the dialog seeds its sharing
  // switch from `isShared` when it opens.
  if (!container || !containerLabel) {
    return null;
  }

  return (
    <MembersDialogView
      mode="manage"
      open={open}
      onOpenChange={onOpenChange}
      containerId={containerId}
      containerLabel={containerLabel}
      isShared={container.is_shared}
      isAiShared={container.is_ai_shared}
      isAiChat={container.container_type === "room"}
      defaultAiPersonaId={container.default_ai_persona_id}
      humanMembers={roster.humanMembers}
      aiMembers={roster.aiMembers}
      availableHumanMembers={roster.availableHumanMembers}
      availableAiMembers={roster.availableAiMembers}
      avatarStorageName={AVATARS_STORAGE_BUCKET}
      onRemoveMember={onRemoveMember}
    />
  );
}
